import { Check, ChevronDown } from 'lucide-react';
import {
  Button,
  type Key,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select as AriaSelect,
  SelectValue,
  Text,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';

import '../Field/Field.css';
import './Select.css';

export interface SelectOption {
  /** The value. An empty string is allowed and is how a filter says "all". */
  id: string;
  label: string;
}

export interface SelectProps {
  label: string;
  /** Hides the label visually, for a filter whose options name themselves. */
  showLabel?: boolean;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  description?: string;
  placeholder?: string;
  isDisabled?: boolean;
}

// React Aria treats an empty key as "nothing selected", but a filter's "All"
// option is a real choice with the value "". It travels under this key.
const EMPTY_KEY = '__rs-empty__';
const toKey = (value: string): Key => (value === '' ? EMPTY_KEY : value);
const fromKey = (key: Key | null): string => (key === null || key === EMPTY_KEY ? '' : String(key));

/** Select picks one value from a short, known list. For more than a dozen
 * options, or free text, a search is kinder. */
export function Select({
  label,
  showLabel = true,
  options,
  value,
  onChange,
  description,
  placeholder,
  isDisabled = false,
}: SelectProps) {
  const { t } = useTranslation('common');
  const empty = options.length === 0;
  return (
    <AriaSelect
      className="rs-field rs-select"
      value={toKey(value)}
      onChange={(key) => onChange(fromKey(key))}
      isDisabled={isDisabled || empty}
      placeholder={empty ? t('state.noOptions') : (placeholder ?? t('actions.choose'))}
    >
      <Label className={showLabel ? 'rs-field__label' : 'rs-visually-hidden'}>{label}</Label>
      <Button className="rs-input rs-select__trigger">
        <SelectValue className="rs-select__value" />
        <ChevronDown aria-hidden="true" />
      </Button>
      {description && (
        <Text slot="description" className="rs-field__description">
          {description}
        </Text>
      )}
      <Popover className="rs-popover" offset={4}>
        <ListBox className="rs-listbox" items={options}>
          {(option) => (
            <ListBoxItem
              id={toKey(option.id)}
              className="rs-listbox__item"
              textValue={option.label}
            >
              <span>{option.label}</span>
              <Check className="rs-listbox__check" aria-hidden="true" />
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}
